import { GetThunkAPI } from '@reduxjs/toolkit';
import { Listing } from '@thurstech/commons/dist/entity/listing';
import { PackageOverride, TicketPackageOverride } from '@thurstech/commons/dist/entity/packageOverride';
import {
  Package,
  Slot,
  SlotType,
  SlotWithResources,
  TicketBookingVenue,
  Venue
} from '@thurstech/commons/dist/entity/venue';
import { GetProductsResponse } from '@thurstech/commons/dist/models/listing';
import { ImageToString } from '@thurstech/commons/dist/utils/types';
import _ from 'lodash';

import axiosInstance from '../../utils/axios';
import { VenueCardFormData } from '../../validation/operations/products/venueCard';
import { AsyncThunkConfig, withApiErrorHandler } from '../common';

export const addListing = async (
  listing: ImageToString<Omit<Listing, 'propId'>>,
  { getState }: GetThunkAPI<AsyncThunkConfig>
) => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.post(`/prop/${propId}/listing`, listing);
    return response.data;
  }, propId);
};

export const getListing = async (
  listingId: string,
  { getState }: GetThunkAPI<AsyncThunkConfig>
): Promise<Listing> => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.get(`/prop/${propId}/listing/${listingId}`);
    return response.data;
  }, propId);
};

export const updateListing = async (
  { listingId, listing }: { listingId: string; listing: ImageToString<Omit<Listing, 'propId'>> },
  { getState }: GetThunkAPI<AsyncThunkConfig>
) => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.put(`/prop/${propId}/listing/${listingId}`, listing);
    return response.data;
  }, propId);
};

export type VenueMeta = Pick<
  TicketBookingVenue,
  'id' | 'address' | 'phoneNumber' | 'slots' | 'title' | 'city'
>;
export const getVenuesMeta = async (
  listingId: string,
  { getState }: GetThunkAPI<AsyncThunkConfig>
): Promise<VenueMeta[]> => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.get(`/prop/${propId}/listing/${listingId}/venues-meta`);
    return response.data;
  }, propId);
};

export const getProducts = async (
  _: void,
  { getState }: GetThunkAPI<AsyncThunkConfig>
): Promise<GetProductsResponse> => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.get(`/prop/${propId}/products`);
    return response.data;
  }, propId);
};

export const updateVenuePackages = async (
  {
    listingId,
    venueId,
    packages
  }: {
    listingId: string;
    venueId: string;
    packages: Pick<VenueCardFormData['packages'][number], 'id' | 'discount' | 'price' | 'resources'>[];
  },
  { getState }: GetThunkAPI<AsyncThunkConfig>
) => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.put(
      `/prop/${propId}/listing/${listingId}/venue/${venueId}/packages`,
      { packages }
    );
    return response.data;
  }, propId);
};

export const addVenue = async (
  { listingId, venue }: { listingId: string; venue: Omit<Venue, 'propId' | 'listingId'> },
  { getState }: GetThunkAPI<AsyncThunkConfig>
) => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.post(`/prop/${propId}/listing/${listingId}/venues`, venue);
    return response.data;
  }, propId);
};

export const getVenue = async (
  { listingId, venueId }: { listingId: string; venueId: string },
  { getState }: GetThunkAPI<AsyncThunkConfig>
) => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.get(`/prop/${propId}/listing/${listingId}/venue/${venueId}`);
    return response.data;
  }, propId);
};

export const updateVenue = async (
  {
    listingId,
    venueId,
    venue
  }: {
    listingId: string;
    venueId: string;
    venue: Omit<Venue, 'propId' | 'listingId' | 'packages' | 'isResourcesLinkedToVenue'>;
  },
  { getState }: GetThunkAPI<AsyncThunkConfig>
) => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.put(`/prop/${propId}/listing/${listingId}/venue/${venueId}`, venue);
    return response.data;
  }, propId);
};

export const savePackage = async (
  {
    listingId,
    venueId,
    packageDetail
  }: {
    listingId: string;
    venueId: string;
    packageDetail: Package;
  },
  { getState }: GetThunkAPI<AsyncThunkConfig>
): Promise<{ id?: string }> => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.put(
      `/prop/${propId}/listing/${listingId}/venue/${venueId}/package`,
      packageDetail
    );
    return response.data;
  }, propId);
};

export const deletePackage = async (
  {
    listingId,
    venueId,
    packageId
  }: {
    listingId: string;
    venueId: string;
    packageId: string;
  },
  { getState }: GetThunkAPI<AsyncThunkConfig>
) => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.delete(
      `/prop/${propId}/listing/${listingId}/venue/${venueId}/package/${packageId}`
    );
    return response.data;
  }, propId);
};

export const addSlot = async (
  {
    listingId,
    venueId,
    slot,
    isResourcesLinkedToVenue
  }: {
    listingId: string;
    venueId: string;
    slot: Pick<Slot, 'sessions'> | Pick<SlotWithResources, 'sessions' | 'resources'>;
    isResourcesLinkedToVenue?: boolean;
  },
  { getState }: GetThunkAPI<AsyncThunkConfig>
): Promise<{ id: string }> => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.post(`/prop/${propId}/listing/${listingId}/venue/${venueId}/slot`, {
      ...slot,
      isResourcesLinkedToVenue
    });
    return response.data;
  }, propId);
};

export const updateSlot = async (
  {
    listingId,
    venueId,
    slot,
    isResourcesLinkedToVenue
  }: {
    listingId: string;
    venueId: string;
    slot: SlotType;
    isResourcesLinkedToVenue?: boolean;
  },
  { getState }: GetThunkAPI<AsyncThunkConfig>
) => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.put(
      `/prop/${propId}/listing/${listingId}/venue/${venueId}/slot/${slot.id!}`,
      { ..._.omit(slot, 'id'), isResourcesLinkedToVenue }
    );
    return response.data;
  }, propId);
};

export const updatePackageOverrides = async (
  {
    listingId,
    venueId,
    data
  }: {
    listingId: string;
    venueId: string;
    data: Omit<TicketPackageOverride, 'propId' | 'packageId' | 'detail'> & {
      details: Array<PackageOverride['detail'] & { id?: string }>;
    };
  },
  { getState }: GetThunkAPI<AsyncThunkConfig>
): Promise<{ id: string }> => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.put(
      `/prop/${propId}/listing/${listingId}/venue/${venueId}/package/overrides`,
      data
    );
    return response.data;
  }, propId);
};

export const getPackageOverrides = async (
  {
    listingId,
    venueId,
    slotId
  }: {
    listingId: string;
    venueId: string;
    slotId: string;
  },
  { getState }: GetThunkAPI<AsyncThunkConfig>
): Promise<{ overrides: Array<PackageOverride> }> => {
  const propId = getState().prop.propId;
  return withApiErrorHandler(async () => {
    const response = await axiosInstance.get(
      `/prop/${propId}/listing/${listingId}/venue/${venueId}/package/overrides`,
      { params: { slotId } }
    );
    return response.data;
  }, propId);
};
